import { useContext, useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L, { CRS } from 'leaflet';
import cx from 'classnames';

import {
  imageBounds,
  imageBoundsBig,
  ImageLayerType,
  useAppSelector,
  preferencesSelector,
} from '../../../store';

import { LeafletContext } from '../../../App';
import { minZoom, maxZoom } from '../../Map';

import './Layer.scss';

/**
 * Calculate zoom level that fills the viewport (cover behavior)
 * instead of fitting the entire bounds inside (contain behavior).
 */
function getCoverZoom(map: L.Map, bounds: L.LatLngBounds): number {
  const size = map.getSize();

  // If map size is not yet available, return a reasonable default
  if (size.x === 0 || size.y === 0) {
    return minZoom;
  }

  // Get the zoom level that would fit the bounds (contain behavior)
  const containZoom = map.getBoundsZoom(bounds);

  // Calculate the aspect ratios
  const boundsSize = map.project(bounds.getNorthEast(), containZoom)
    .subtract(map.project(bounds.getSouthWest(), containZoom));

  const viewportAspect = size.x / size.y;
  const boundsAspect = Math.abs(boundsSize.x / boundsSize.y);

  // Calculate additional zoom needed to cover instead of contain
  let additionalZoom = 0;
  if (viewportAspect > boundsAspect) {
    // Viewport is wider than bounds - need to zoom more to fill width
    additionalZoom = Math.log2(viewportAspect / boundsAspect);
  } else {
    // Viewport is taller than bounds - need to zoom more to fill height
    additionalZoom = Math.log2(boundsAspect / viewportAspect);
  }

  // Add small buffer to ensure complete coverage (accounts for rounding)
  const coverZoom = containZoom + additionalZoom + 0.05;

  // Clamp to min/max zoom
  return Math.max(minZoom, Math.min(maxZoom, coverZoom));
}

/**
 * Minimum zoom of an image layer: the zoom showing the whole image, so zooming
 * out never adds margins on both its sides and its top/bottom. The image is
 * shown at its pixel size on the max zoom
 */
export function getContainZoom(map: L.Map, image: Pick<ImageLayerType, 'width' | 'height'>): number {
  const size = map.getSize();
  if (size.x === 0 || size.y === 0 || !image.width || !image.height) return minZoom;

  const containZoom = maxZoom + Math.log2(Math.min(size.x / image.width, size.y / image.height));
  return Math.max(minZoom, Math.min(maxZoom, containZoom));
}

type LayerProps = {
  image: ImageLayerType;
  isActive: boolean;
  setActive: () => void;
  children?: JSX.Element,
};

export function ImageLayer(props: LayerProps) {
  const map = useMap();
  const { image, isActive, setActive, children } = props;
  const preferences = useAppSelector(preferencesSelector);
  const prevFillViewport = useRef(preferences.fillViewport);

  const { activeLayerRef } = useContext(LeafletContext);

  // Effect to handle fillViewport preference changes (live preview)
  useEffect(() => {
    if (!map || !isActive) return;
    // Skip if this is the initial mount (handled by the layer load effect)
    if (prevFillViewport.current === preferences.fillViewport) return;

    prevFillViewport.current = preferences.fillViewport;

    const bounds = imageBounds(map, image);
    if (preferences.fillViewport) {
      const coverZoom = getCoverZoom(map, bounds);
      map.setView(bounds.getCenter(), coverZoom, { animate: true });
    } else {
      map.fitBounds(bounds, { animate: true });
    }
  }, [map, isActive, image, preferences.fillViewport]);

  useEffect(() => {
    if (!map || !isActive) return;
    // do not replace layer if it's the same
    if (activeLayerRef.current.id === image.id) return;

    // If Map has another Layer loaded
    if (activeLayerRef.current.object) {
      // remove it from Map
      map.removeLayer(activeLayerRef.current.object);
      activeLayerRef.current.object = undefined;
      activeLayerRef.current.id = null;
    }
    // In case sidebar is not the same size on this layer
    map.invalidateSize();

    // Add layer for active image to Map
    map.options.crs = CRS.Simple;
    map.setMaxZoom(maxZoom);
    map.setMinZoom(minZoom);

    const bounds = imageBounds(map, image);

    if (image.tiles) {
      // Image, tiled
      activeLayerRef.current.object = L.tileLayer(
        image.tiles,
        { minZoom, maxZoom, bounds }
      );
    } else {
      // Image, not tiled
      if (isActive) {
        activeLayerRef.current.object = L.imageOverlay(
          image.fullImage.original,
          bounds,
        );
      }
    }
    activeLayerRef.current.id = image.id;
    map.addLayer(activeLayerRef.current.object!);
    map.setMaxBounds(imageBoundsBig(map, image));

    // Delay to ensure map size is calculated after render
    requestAnimationFrame(() => {
      map.invalidateSize();
      // Update the ref to current value after initial load
      prevFillViewport.current = preferences.fillViewport;
      if (preferences.fillViewport) {
        // Cover fit - fill the viewport (may crop edges)
        const coverZoom = getCoverZoom(map, bounds);
        map.setView(bounds.getCenter(), coverZoom, { animate: false });
      } else {
        // Contain fit - show entire image (may have borders)
        map.fitBounds(bounds);
      }
    });
    // preferences.fillViewport changes are handled by the live preview effect above
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, image, activeLayerRef, isActive]);

  // Keep the min zoom to the whole image as the map is resized (window, sidebar)
  useEffect(() => {
    if (!map || !isActive) return;
    const updateMinZoom = () => map.setMinZoom(getContainZoom(map, image));
    updateMinZoom();
    map.on('resize', updateMinZoom);
    return () => {
      map.off('resize', updateMinZoom);
    };
  }, [map, image, isActive]);

  return (
    <label
      className="image"
      onClick={() => {
        if (!isActive) {
          setActive();
        } else {
          // Used when clicking on already active layer
          // Center map on its original position
          const bounds = imageBounds(map, image);
          if (preferences.fillViewport) {
            const coverZoom = getCoverZoom(map, bounds);
            map.setView(bounds.getCenter(), coverZoom);
          } else {
            map.fitBounds(bounds);
          }
        }
      }}
    >
      <figure className={cx({ checked: isActive })}>
        <img
          src={image.fullImage.thumb}
          alt={image.name}
        />
        <figcaption title={image.name}>{image.name}</figcaption>
        {children}
      </figure>
    </label>
  );
}

